import { NgTemplateOutlet } from '@angular/common';
import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { LinkColor, LinkUnderline, QueryParams } from '@freelancer/ui/link';
import { textVariants } from '@freelancer/ui/text';
import {
  FontColor,
  FontWeight,
  TextAlign,
  TextSize,
  TextTransform,
} from '@freelancer/ui/text';
import { MaxLinesDirective } from '@freelancer/ui/text/max-lines.directive';
import { IconColor, IconModule, IconSize } from '../icon';
import { IconConfig, TagColor, TagSize, TagType } from './tag.types';
import { LinkModule } from '../link/link.module';

@Component({
  standalone: true,
  imports: [IconModule, LinkModule, MaxLinesDirective, NgTemplateOutlet],
  selector: 'fl-tag',
  template: `
    @if (link) {
      <fl-link
        class="Tag"
        [attr.data-tag-borderless]="borderless"
        [attr.data-tag-color]="color"
        [attr.data-tag-dark-mode]="darkMode"
        [attr.data-tag-hover-border]="hoverBorder"
        [attr.data-tag-type]="attrType"
        [attr.data-tag-size]="size"
        [attr.data-tag-size-tablet]="sizeTablet"
        [attr.data-tag-size-desktop]="sizeDesktop"
        [attr.data-tag-size-desktop-large]="sizeDesktopLarge"
        [attr.data-tag-size-desktop-xlarge]="sizeDesktopXLarge"
        [attr.data-tag-size-desktop-xxlarge]="sizeDesktopXXLarge"
        [attr.data-tag-invalid]="isInvalid"
        [color]="borderless === true ? LinkColor.DEFAULT : LinkColor.INHERIT"
        [link]="link"
        [newTab]="newTab"
        [queryParams]="queryParams"
        [underline]="LinkUnderline.NEVER"
      >
        <ng-container *ngTemplateOutlet="content"></ng-container>
      </fl-link>
    }

    @if (!link) {
      <div
        class="Tag"
        [class.IsSelected]="selected"
        [attr.data-tag-color]="color"
        [attr.data-tag-invalid]="isInvalid"
        [attr.data-tag-borderless]="borderless"
        [attr.data-tag-dark-mode]="darkMode"
        [attr.data-tag-hover-border]="hoverBorder"
        [attr.data-tag-type]="attrType"
        [attr.data-tag-size]="size"
        [attr.data-tag-size-tablet]="sizeTablet"
        [attr.data-tag-size-desktop]="sizeDesktop"
        [attr.data-tag-size-desktop-large]="sizeDesktopLarge"
        [attr.data-tag-size-desktop-xlarge]="sizeDesktopXLarge"
        [attr.data-tag-size-desktop-xxlarge]="sizeDesktopXXLarge"
      >
        <ng-container *ngTemplateOutlet="content"></ng-container>
      </div>
    }

    <ng-template #content>
      <div
        class="ContentTextSizeSetterContainer"
        [attr.data-tag-size]="size"
        [attr.data-tag-size-tablet]="sizeTablet"
        [attr.data-tag-size-desktop]="sizeDesktop"
        [attr.data-tag-size-desktop-large]="sizeDesktopLarge"
        [attr.data-tag-size-desktop-xlarge]="sizeDesktopXLarge"
        [attr.data-tag-size-desktop-xxlarge]="sizeDesktopXXLarge"
        [attr.data-truncate]="truncate"
      >
        @if (iconConfig) {
          <fl-icon
            [class.mr-xxxsmall]="!iconOnly"
            [name]="iconConfig.name"
            [size]="iconConfig.size"
            [color]="iconConfig?.color"
            [useIconFont]="!!iconConfig.useIconFont"
            [label]="iconConfig.label"
          ></fl-icon>
        }
        <div
          [class]="tagTextClasses"
          [flMaxLines]="truncate ? maxLines : undefined"
          [maxHeight]="TextSize.INHERIT"
          [class.mr-xxxsmall]="attrType === TagType.DISMISSABLE"
          [class.mr-none]="!(attrType === TagType.DISMISSABLE)"
        >
          <div class="Content">
            <ng-content></ng-content>
          </div>
        </div>
      </div>
      @if (attrType === TagType.DISMISSABLE) {
        <fl-icon
          i18n-label="Remove tag button"
          label="Remove"
          class="CloseIcon"
          [name]="'ui-close'"
          [color]="IconColor.INHERIT"
          [size]="IconSize.SMALL"
          (click)="handleDismiss($event)"
          [useIconFont]="true"
        ></fl-icon>
      }
    </ng-template>
  `,
  styleUrls: ['./tag.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagComponent implements OnInit, OnChanges {
  TextSize = TextSize;
  IconColor = IconColor;
  IconSize = IconSize;
  LinkColor = LinkColor;
  LinkUnderline = LinkUnderline;
  TagType = TagType;

  /**
   * Used for styling purposes only so we don't need to
   * override the [type] input when [link] is provided
   */
  attrType: TagType;

  @Input() color = TagColor.LIGHT;
  @Input() link?: string;
  @Input() newTab?: boolean;
  @Input() queryParams?: QueryParams;
  @Input() isInvalid?: boolean;
  @Input() type = TagType.DEFAULT;
  @Input() size = TagSize.MID;
  @Input() sizeTablet?: TagSize;
  @Input() sizeDesktop?: TagSize;
  @Input() sizeDesktopLarge?: TagSize;
  @Input() sizeDesktopXLarge?: TagSize;
  @Input() sizeDesktopXXLarge?: TagSize;
  @HostBinding('class.IsSelected')
  @Input()
  selected = false;
  @Input() maxLines = 1;
  @Input() textAlign?: TextAlign;
  @Input() textTransform?: TextTransform;
  @Input() fontWeight: FontWeight = FontWeight.NORMAL;
  @Input() iconConfig?: IconConfig;
  @Input() hoverBorder? = true;
  /** Whether the tag should be truncated */
  @Input() truncate? = true;

  @HostBinding('attr.data-tag-borderless')
  @Input()
  borderless = false;

  @HostBinding('attr.data-tag-dark-mode')
  @Input()
  darkMode = false;

  /**
   * Renders the tag as a circular icon-only button.
   * Use with iconConfig and selected for icon selector patterns.
   */
  @HostBinding('class.IsIconOnly')
  @Input()
  iconOnly = false;

  @Output() dismiss = new EventEmitter<void>();

  tagTextClasses: string;

  ngOnInit(): void {
    this.computeClasses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.link) {
      this.attrType = TagType.CLICKABLE;
    } else {
      this.attrType = this.type;
    }

    if (
      'isInvalid' in changes ||
      'textAlign' in changes ||
      'textTransform' in changes ||
      'fontWeight' in changes
    ) {
      this.computeClasses();
    }
  }

  private computeClasses(): void {
    this.tagTextClasses = textVariants({
      color: this.isInvalid ? FontColor.ERROR : FontColor.INHERIT,
      align: this.textAlign,
      transform: this.textTransform,
      size: TextSize.INHERIT,
      weight: this.fontWeight,
    });
  }

  handleDismiss(event: Event): void {
    event.stopPropagation();
    this.dismiss.next();
  }
}
